import {toast} from 'react-toastify'
import PropTypes from 'prop-types'
import React from 'react'
import {
  Box,
  Text,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Button,
  CloseButton,
} from '@chakra-ui/react'

import {createPayerArrangment} from '../../../api'
import {ToastMessage} from '../Toast'
import PayerArrangementDetailsForm from './PayerArrangementDetailsForm'

import {useAuthProvider} from '../../contexts/authentication'
import {RadioCard, RadioCardGroup} from '../RadioCard'

const Step = {
  success: 'success',
  initial: 'initial',
  details: 'details',
}

const AdditionalInformationRequiredPayerArrangementTypes = {
  customer_promised_to_pay: 'customer_promised_to_pay',
  marmalade_can_contact: 'marmalade_can_contact',
}

export const PayerArrangementType = {
  customer_promised_to_pay: 'customer_promised_to_pay',
  marmalade_can_contact: 'marmalade_can_contact',
  customer_dispute: 'customer_dispute',
  invoice_not_completed: 'invoice_not_completed',
  invoice_error: 'invoice_error',
  supplier_will_settle: 'supplier_will_settle',
}

const AddOverdueDetailsModal = ({invoice, isOpen, onClose, beforeClose}) => {
  const {authenticityToken} = useAuthProvider()

  const [step, setStep] = React.useState(Step.initial)
  const [selectedType, setSelectedType] = React.useState()

  const handleClose = () => {
    if (beforeClose) beforeClose()
    onClose()
    setStep(Step.initial)
  }

  const handleError = () => {
    toast.error(
      <ToastMessage>
        Unable to add details for <strong>{invoice.number}</strong>. Please try
        again later.
      </ToastMessage>,
    )
  }

  const addPayerArrangment = async (values) => {
    try {
      await createPayerArrangment({
        ...values,
        invoiceId: invoice.id,
        authenticityToken,
      })

      setStep(Step.success)
    } catch {
      handleError()
    }
  }

  const handleClick = () => {
    if (
      Object.values(
        AdditionalInformationRequiredPayerArrangementTypes,
      ).includes(selectedType)
    ) {
      setStep(Step.details)
    } else {
      addPayerArrangment({
        type: selectedType,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} size="xl" onClose={handleClose} preserveScrollBarGap>
      <ModalOverlay zIndex="modal" />
      <ModalContent borderRadius="lg" py="sm">
        <ModalHeader fontSize="lg">
          <Stack isInline justifyContent="space-between">
            <>What should we know about {invoice.number}?</>
            <CloseButton onClick={handleClose} />
          </Stack>
        </ModalHeader>

        <ModalBody>
          <Stack spacing="sm">
            <HStack
              spacing="lg"
              pb="sm"
              borderBottom="1px"
              borderColor="neutral.glitter"
            >
              <Box>
                <Text fontWeight="medium">Customer</Text>
                <Text color="neutral.60">{invoice.contactName}</Text>
              </Box>

              <Box>
                <Text fontWeight="medium">Due amount</Text>
                <Text color="neutral.60">{invoice.dueAmount}</Text>
              </Box>
            </HStack>

            {step === Step.initial && (
              <>
                <Text fontWeight="medium">
                  Please select from below the reasons your customers are not
                  paying on time, so we can support you to get this rectified.
                </Text>

                <RadioCardGroup
                  name="selectedType"
                  value={selectedType}
                  onChange={(event) => setSelectedType(event.target.value)}
                >
                  <RadioCard
                    compact
                    align="left"
                    value={PayerArrangementType.customer_dispute}
                  >
                    My customer has issues with the goods / services provided
                  </RadioCard>

                  <RadioCard
                    compact
                    align="left"
                    value={PayerArrangementType.invoice_not_completed}
                  >
                    Items not delivered / services not completed
                  </RadioCard>

                  <RadioCard
                    compact
                    align="left"
                    value={PayerArrangementType.invoice_error}
                  >
                    Error on original invoice
                  </RadioCard>

                  <RadioCard
                    compact
                    align="left"
                    value={PayerArrangementType.customer_promised_to_pay}
                  >
                    My customer has promised to pay
                  </RadioCard>

                  <RadioCard
                    compact
                    align="left"
                    value={PayerArrangementType.marmalade_can_contact}
                  >
                    Marmalade can contact my customer on my behalf
                  </RadioCard>

                  <RadioCard
                    compact
                    align="left"
                    value={PayerArrangementType.supplier_will_settle}
                  >
                    I&apos;ll settle this invoice on behalf of my customer
                  </RadioCard>
                </RadioCardGroup>

                <Box pt="xs">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleClick}
                    isDisabled={!selectedType}
                  >
                    Confirm
                  </Button>
                </Box>
              </>
            )}

            {step === Step.details && (
              <PayerArrangementDetailsForm
                onSubmit={addPayerArrangment}
                onBack={() => setStep(Step.initial)}
                type={selectedType}
              />
            )}

            {step === Step.success && (
              <>
                <Text>
                  {selectedType === PayerArrangementType.marmalade_can_contact
                    ? 'Thanks for letting us know. Once we hear back from your cutomer, we will update you on the status of payment.'
                    : 'Thanks for letting us know, we’ll verify the information you provided.'}
                </Text>

                <Box pt="lg">
                  <Button variant="secondary" size="lg" onClick={handleClose}>
                    Done
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

AddOverdueDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  beforeClose: PropTypes.func,
  invoice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
    contactName: PropTypes.string.isRequired,
    dueAmount: PropTypes.string.isRequired,
  }).isRequired,
}

AddOverdueDetailsModal.defaultProps = {
  beforeClose: null,
}

export default AddOverdueDetailsModal
